<template>
    <div id="chat-theme-editor" :key="preferredThemeColor" :class="{ [hoveredPart]: true }">
        <div class="left">
            <div class="d-flex gap-5 featur-item">
                <FeatureItem
                    :title="$t('message.theme')"
                    icon="ThemeIcon"
                    v-model="preferredThemeColor"
                    type="select"
                    :options="[
                        { id: 'custom', label: 'Custom Theme' },
                        ...allThemeColors.map((x) => ({ id: x, label: x })).filter((x) => x.id !== 'custom'),
                    ]"
                    :default-select-option-placeholder="$t('message.selectATheme')"
                    :description="$t('settings.themeDescription')"
                    :show-null-select-option="false"
                    @click="(e) => e.stopPropagation()"
                />
                <FeatureItem
                    :title="$t('theme.font')"
                    icon="FontIcon"
                    v-model="fontCustom"
                    type="select"
                    :options="fonts.map((x) => ({ id: x, label: x }))"
                    :default-select-option-placeholder="$t('message.selectATheme')"
                    :description="$t('settings.fontDescription')"
                />
            </div>

            <div class="colors" :key="preferredThemeColor" style="margin: 50px">
                <ColorPickerItem
                    v-for="color in getColors()"
                    :key="color.id"
                    :title="color.title"
                    :value="readColorValue(color.id)"
                    :rawColor="readRawColorValue(color.id)"
                    @input="(value) => setColorValue(color.id, value)"
                    :opacity-slider="color.configurableOpacity"
                    :on-mouse-enter="() => (hoveredPart = color.id)"
                    :on-mouse-leave="() => (hoveredPart = null)"
                />
            </div>
        </div>
        <div class="preview">
            <div class="demo-visitor-wrapper">
                <DemoVisitor />
            </div>
        </div>
    </div>
</template>

<script>
import { preConfiguredThemesV2 } from "../../../../../utils/chatTheme";
import * as _ from "lodash";
import { mapState } from "vuex";
import { log2 } from "../../../../../utils/log2";
import FeatureItem from "../FeatureItem.vue";
import ColorPickerItem from "./ColorPickerItem.vue";
import { COLOR_ALPHA_MAP, COLOR_ALPHA_MAP_REVERSE } from "../../../../../utils/colorUtils";
import DemoVisitor from "../../../../../components/DemoVisitor.vue";

const log = log2("ChatThemeEditor");
export default {
    name: "ChatThemeEditor",
    components: {
        FeatureItem,
        ColorPickerItem,
        DemoVisitor,
    },
    props: {
        updateSettings: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            hoveredPart: "",
            isCustomTheme: false,
            previewLoading: false,
            isPreviewActive: !!window.supsis,
            alphaMap: COLOR_ALPHA_MAP,
            alphaMapRev: COLOR_ALPHA_MAP_REVERSE,
            fonts: [
                "Comic Sans MS",
                "Poppins",
                "Open Sans",
                "Montserrat",
                "Roboto",
                "Montserrat",
                "Lato",
                "Raleway",
                "Source Sans Pro",
                "Ubuntu",
                "PT Sans",
                "Merriweather",
                "Playfair Display",
                "Oswald",
                "Noto Sans",
                "Fira Sans",
                "Cabin",
                "Quicksand",
                "Nunito",
                "Archivo",
            ],
            selectedFont: "",
        };
    },
    methods: {
        getColors() {
            return [
                {
                    id: "headerColorSecond",
                    title: this.$t("theme.headerColorSecond"),
                    configurableOpacity: true,
                },
                { id: "headerColorFirst", title: this.$t("theme.headerColorFirst") },
                { id: "backgroundColorSecond", title: this.$t("theme.backgroundColorFirst") },
                { id: "backgroundColorFirst", title: this.$t("theme.backgroundColorSecond") },
                { id: "representativeBubbleColor", title: this.$t("theme.bubbleBackground") },
                { id: "representativeTextColor", title: this.$t("theme.bubbleText") },
                { id: "customerBubbleColor", title: this.$t("theme.bubbleVisitorBackground") },
                { id: "customerTextColor", title: this.$t("theme.bubbleVisitorText") },
                { id: "headerTextColor", title: this.$t("theme.textColor") },
                { id: "errorColor", title: this.$t("theme.errorTheme") },
                { id: "formColorText", title: this.$t("theme.formColor") },
                { id: "buttonColor", title: this.$t("theme.buttonColor") },
                { id: "chatBotButtonText", title: this.$t("theme.chatBotButtonText") },
                { id: "chatBotButtonColor", title: this.$t("theme.chatBotButtonColor") },
            ];
        },

        addAlpha(hex, e) {
            hex = hex.substring(1).split("");
            if (hex.length === 8) {
                hex.pop();
                hex.pop();
            }
            hex = "#" + hex.join("");
            return hex + this.alphaMap[e];
        },

        readColorValue(id) {
            const key = `chatCustomizationSettings.versionV2Theme.colors.${id}`;
            return _.get(this.settings, key, "#000000").substring(0, 7);
        },
        readRawColorValue(id) {
            const key = `chatCustomizationSettings.versionV2Theme.colors.${id}`;
            return _.get(this.settings, key, "#000000");
        },
        setColorValue(id, value) {
            if (this.preferredThemeColor !== "custom") {
                this.updateSettings("chatCustomizationSettings.preferredThemeColor", "custom");
            }
            const key = `chatCustomizationSettings.versionV2Theme.colors.${id}`;
            this.updateSettings(key, value);
        },
    },
    computed: {
        ...mapState({
            allThemeColors: (state) => state.setting.allThemeColors,
            settings: (state) => state.setting.settings,
            settingInitialized: (state) => state.setting.initialized,
            site: (state) => state.site.site,
        }),
        preferredThemeColor: {
            get() {
                return _.get(this.settings.chatCustomizationSettings, "preferredThemeColor");
            },
            set(value) {
                if (value !== "custom") {
                    const nextColors = preConfiguredThemesV2[value];
                    const currentColors = _.get(this.settings, "chatCustomizationSettings.versionV2Theme.colors");
                    const newColors = Object.assign({}, currentColors, nextColors);
                    this.updateSettings("chatCustomizationSettings.versionV2Theme.colors", newColors, true);
                }

                this.updateSettings("chatCustomizationSettings.preferredThemeColor", value, true);
            },
        },

        fontCustom: {
            get() {
                return _.get(this.settings, "chatCustomizationSettings.versionV2Theme.fonts.fontCustom", "Poppins");
            },
            set(value) {
                this.updateSettings("chatCustomizationSettings.versionV2Theme.fonts.fontCustom", value);
            },
        },
    },
};
</script>

<style scoped lang="scss">
#chat-theme-editor {
    display: flex;
    gap: 5px;

    .left {
        border-right: 1px solid #ebebeb;
        padding-right: 10px;
    }

    .feature {
        max-width: unset !important;
    }

    .left {
        display: flex;
        flex-direction: column;
        gap: 10px;
        flex: 3;
    }

    .preview {
        flex: 1;
        border-radius: 6px;
        padding-left: 0;
        position: relative;
        width: 100%;
        background-image: url(data:image/gif;base64,R0lGODlhEAAQAJEAAAAAAP////X19f///yH5BAEAAAMALAAAAAAQABAAAAIflG+hq4jM3IFLJhqswNly/XkcBpIiVaInlLJr9FZWAQA7);

        background-repeat: repeat;
        height: 100%;

        .demo-visitor-wrapper {
            width: 300px;
            min-height: 590px !important;
            margin: 0 auto;
            padding: 10px 0;
        }
    }

    .colors {
        max-width: 1000px;
        display: flex;
        gap: 5px;
        flex-wrap: wrap;
    }

    .btnColor {
        margin: 14px;
        border-color: var(--color-bg-primary-default) !important;
        color: var(--color-bg-primary-default) !important;
    }
}

.featur-item {
    max-width: 900px;
    margin-bottom: 10px;
}
</style>
<style lang="scss">
#chat-theme-editor {
    &.headerColorFirst,
    &.headerColorSecond {
        .demo-header {
            box-shadow: 2px -9px 19px 24px rgb(30 30 45 / 52%) !important;
        }
    }

    &.backgroundColorFirst,
    &.backgroundColorSecond {
        .chat-body {
            box-shadow: 2px -9px 19px 24px rgb(30 30 45 / 52%) !important;
        }
    }

    &.representativeTextColor,
    &.representativeBubbleColor {
        .representative-message-container {
            box-shadow: 2px -9px 19px 24px rgb(30 30 45 / 52%) !important;
        }
    }

    &.customerBubbleColor,
    &.customerTextColor {
        .chat-body {
            .visitor-message-container {
                &.first {
                    box-shadow: 2px -9px 19px 24px rgb(30 30 45 / 52%) !important;
                }
            }
        }
    }

    &.textColor {
        .editor-wrapper {
            box-shadow: 2px -9px 19px 24px rgb(30 30 45 / 52%) !important;
        }
    }
}
</style>
